import { Destination } from "Constant/destination";

import getDestinationIcon from "helpers/destinations/getDestinationIcon";

export const cannotBeBlank = (language) =>
    language?.tracker_wizard?.cannot_be_blank ?? "Cannot be blank!";

const getUniqueDestinationCodes = (sourcesTypes) =>
    sourcesTypes
        ?.map((source) => source?.destinations)
        ?.flat()
        ?.map((destination) => destination.code)
        ?.filter(
            (destination, index, self) => self.indexOf(destination) === index,
        ) ?? [];

const getDestinationCodesForSourceTypes = (sourcesTypes, sourceTypeCodes) => {
    const areAllSourcesKnown = sourceTypeCodes.every((code) =>
        sourcesTypes?.find((sourceType) => sourceType.code === code),
    );
    if (!sourceTypeCodes.length || !areAllSourcesKnown) {
        return getUniqueDestinationCodes(sourcesTypes);
    }
    return getUniqueDestinationCodes(
        sourcesTypes.filter((sourceType) =>
            sourceTypeCodes.includes(sourceType.code),
        ),
    );
};

/**
 * Destination codes reachable from the tracker's sources, or from
 * `sourceTypeCodes` when the sources aren't attached to a tracker yet (the
 * wizard). Without sources (or with partly unknown ones) every destination of
 * every source type is available.
 */
export const getAvailableDestinationCodes = ({
    sourcesTypes,
    tracker,
    trackerId,
    sourceTypeCodes,
}) => {
    if (sourceTypeCodes) {
        return getDestinationCodesForSourceTypes(sourcesTypes, sourceTypeCodes);
    }

    const trackerSources = tracker?.tracker?.sources;
    if (!trackerId || trackerSources?.length === 0) {
        return getUniqueDestinationCodes(sourcesTypes);
    }
    if (!trackerSources || !sourcesTypes) return [];

    return getDestinationCodesForSourceTypes(
        sourcesTypes,
        trackerSources.map((source) => source.source_type?.code),
    );
};

export const getDestinationTypeOptions = (destinationsTypes) =>
    (destinationsTypes ?? []).map((item) => ({
        value: item?.code,
        label: item?.name,
        image: getDestinationIcon(item?.code),
    }));

export const getPixelIdLabel = (destinationCode, language) => {
    switch (destinationCode) {
        case Destination.ttoffline:
            return (
                language?.destination_modal?.offline_event_set_id ??
                "Offline Event Set Id"
            );
        case Destination.pnpixel:
            return "Pinterest Tag ID";
        case Destination.pncapi:
            return "";
        case Destination.ttapp:
            return "TikTok APP ID";
        default:
            return language?.destination_modal?.pixel_id ?? "Pixel id";
    }
};

export const getPixelIdPlaceholder = (destinationCode, language) => {
    switch (destinationCode) {
        case Destination.ttoffline:
            return (
                language?.destination_modal?.offline_event_set_id ??
                "Offline Event Set Id"
            );
        case Destination.pnpixel:
            return "Tag id";
        case Destination.pncapi:
            return "Ad Account id";
        default:
            return language?.destination_modal?.pixel_id ?? "Pixel id";
    }
};

/** Facebook Business Extension `extras` for the FB SDK login. */
export const buildFacebookBeExtra = (externalBusinessId, business) => ({
    setup: {
        external_business_id: externalBusinessId,
        timezone: "UTC",
        currency: business.currency,
        business_vertical: business.vertical,
        channel: "SIGNALS",
    },
    business_config: {
        business: {
            name: business.name,
        },
    },
    repeat: false,
});

/** Created destinations from a create response (`data.data` or `data`). */
export const getCreatedDestinations = (response) =>
    response?.data?.data ?? response?.data;

/** LinkedIn / Microsoft Bing auto-matched events to selectable mappings. */
export const mapAutoMatchedToMappings = (autoMatchedEvents) =>
    autoMatchedEvents.map((event) => ({
        conversion_id: event.conversion.id?.toString(),
        event: event.code,
        conversion_name: event.conversion.name,
    }));

export const toConversionMappingsPayload = (mappings) => ({
    mappings: mappings.map((mapping) => ({
        conversion_id: mapping.conversion_id?.toString(),
        event: mapping.event?.toString(),
        conversion_name: mapping.conversion_name,
    })),
});

export const findConversionName = (conversions, conversionId) => {
    const conversion = conversions.find(
        (item) => item.id?.toString() === conversionId?.toString(),
    );
    return conversion?.name || conversion?.conversion_name || "";
};

export const buildImpactMappingsPayload = (impactFields) => ({
    mappings: impactFields?.map(
        ({ event_name, campaign_id, action_tracker_id }) => ({
            event_name: event_name?.value,
            campaign_id: campaign_id?.value,
            action_tracker_id: action_tracker_id?.value,
        }),
    ),
});

/** Flattens each field's `{ key: { value } }` parameters to `{ key: value }`. */
export const buildSignalSightTaskPayload = (name, fields) => ({
    name,
    mappings:
        fields?.map((field) => {
            const parameters = {};
            if (field.parameters?.value) {
                Object.entries(field.parameters.value).forEach(
                    ([key, paramData]) => {
                        parameters[key] = paramData.value;
                    },
                );
            }
            return {
                event: field.event_name?.value,
                action: field.action_code?.value,
                parameters,
            };
        }) || [],
});

export const buildPixelTypeNoticeHtml = (pixelId) =>
    '<p style="text-align:start">Please convert it to CRM pixel by following these instructions:</p>' +
    '<ol style="text-align: start; list-style: auto; padding-left: 20px; display: flex; flex-direction: column; gap: 5px;">' +
    '<li><a href="https://www.facebook.com/events_manager2/" style="color: #5353ec" target="_blank">Go to Events Manager</a></li>' +
    "<li>Click the green Plus icon or Connect Data Sources from the menu.</li>" +
    "<li>A Connect a New Data Source pop-up window will appear. Select CRM and click Connect.</li>" +
    "<li>A Connect your CRM for conversion leads pop-up window will appear. Read the instructions and benefits from conversion leads and then click Next.</li>" +
    "<li>Select the Meta Pixel with id <span>" +
    `${pixelId}` +
    "</span> and click Continue.</li>" +
    "</ol>";
